import React, { useState } from 'react';
import { Text, View, ScrollView, RefreshControl } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { deleteTaskAssignmentTemplate } from '@/data/tasks';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import { useRouter } from 'expo-router';
import { showAlert } from '@/utils/showAlert';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';

export default function TaskTemplatesScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);

    const { data: templates, isFetching } = Query.taskAssignmentTemplates.all();
    const { data: loggedInUser } = Query.people.loggedInUser();

    const deleteMutation = useMutation({
        mutationFn: deleteTaskAssignmentTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskAssignmentTemplates.all() });
        },
    });

    const onRefresh = () => {
        setRefreshing(true);
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskAssignmentTemplates.all() });
        setRefreshing(false);
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Text style={styles.headerTitle}>Assignment Templates</Text>
                <Text style={styles.headerSubtitle}>
                    Save and reuse common volunteer assignments for your teams
                </Text>
            </View>

            <ScrollView
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
                contentContainerStyle={styles.listContent}
                keyboardShouldPersistTaps="handled"
            >
                {isFetching ? (
                    <EmptyListPlaceholder message="Loading templates..." />
                ) : templates?.length === 0 ? (
                    <EmptyListPlaceholder
                        message={"No templates yet.\nCreate one for a team from this screen."}
                    />
                ) : (
                    templates?.map((template) => (
                        <View key={template.id} testID={"TemplateCard-" + template.name} style={styles.card}>
                            <View style={styles.cardHeader}>
                                <View style={styles.cardIcon}>
                                    <Unicon name="copy-outline" size={20} colorVariant="primary" />
                                </View>
                                <View style={styles.cardInfo}>
                                    <Text style={styles.cardTitle}>{template.name}</Text>
                                    <Text style={styles.cardSubtitle}>
                                        {template.VolunteerTeam?.name || 'Unknown Team'} · {template.Members?.length || 0} assignments
                                    </Text>
                                </View>
                                {loggedInUser?.can?.("Update Tasks") && (
                                    <DropdownMenu
                                        testID={"TemplateCardMenu-" + template.name}
                                        options={[
                                            {
                                                name: "Edit Template",
                                                icon: dropdownIcons.edit,
                                                onSelect: () => router.push({ pathname: '/tasks/templates/create', params: { id: template.id } }),
                                            },
                                            {
                                                name: "Delete Template",
                                                icon: dropdownIcons.delete,
                                                destructive: true,
                                                onSelect: () => {
                                                    showAlert({
                                                        title: "Delete Template",
                                                        subtitle: `Are you sure you want to delete "${template.name}"?`,
                                                        isDestructive: true,
                                                        confirmText: "Delete",
                                                        onConfirm: () => deleteMutation.mutate(template.id),
                                                    });
                                                },
                                            },
                                        ]}
                                    />
                                )}
                            </View>

                            <View style={styles.membersPreview}>
                                {template.Members?.slice(0, 5).map((member) => (
                                    <View key={member.id} style={styles.memberChip}>
                                        <Text style={styles.memberChipText} numberOfLines={1}>
                                            {member.Task?.title}: {member.Person?.firstName} {member.Person?.lastName}
                                        </Text>
                                    </View>
                                ))}
                                {(template.Members?.length || 0) > 5 && (
                                    <Text style={styles.moreText}>+{template.Members.length - 5} more</Text>
                                )}
                            </View>
                        </View>
                    ))
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    header: {
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
    },
    headerTitle: {
        fontSize: 20,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    headerSubtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 4,
    },
    listContent: {
        padding: 16,
        paddingBottom: 40,
        gap: 12,
    },
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        padding: 14,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    cardIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardInfo: {
        flex: 1,
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    cardSubtitle: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    membersPreview: {
        marginTop: 12,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    memberChip: {
        backgroundColor: theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    memberChipText: {
        fontSize: 11,
        color: theme.colors.textSecondary,
    },
    moreText: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        fontStyle: 'italic',
        alignSelf: 'center',
    },
}));
